import { isAbortError, isApiTransportError, isBackendRouteUnavailableError } from '@/lib/apiErrors'
import type { TeamCheckoutQuote } from '@/types/shared'

export type PromoCodeHandoff =
  | { target: 'pro' | 'studio'; quote: TeamCheckoutQuote }
  /** Neither plan accepts the code. */
  | { target: 'invalid'; code: string }
  /** The preview could not be reached; nothing is known about the code yet. */
  | { target: 'unavailable'; code: string }

/**
 * An outage rather than a verdict on the code: no network, an older backend
 * without the preview route, an aborted request, or a 5xx from the preview.
 * A 4xx is the backend rejecting the code for that plan.
 */
export function isPreviewOutage(error: unknown): boolean {
  if (isApiTransportError(error) || isBackendRouteUnavailableError(error) || isAbortError(error)) return true
  const status = (error as { status?: unknown } | null)?.status
  return typeof status === 'number' && status >= 500
}

/**
 * Which plan a handed-off promo code (`/pricing?code=X`) belongs to. Autumn
 * rewards are plan-scoped and the preview route rejects a code for the wrong
 * plan, so the Pro preview is tried first and the Studio (team) preview second.
 * An outage on either step stops the resolution instead of counting as a
 * rejection. Pure over the two preview calls so it can be tested without React.
 */
export async function resolvePromoCodeHandoff(
  code: string,
  preview: {
    pro: (code: string) => Promise<TeamCheckoutQuote>
    studio: (code: string) => Promise<TeamCheckoutQuote>
  },
  isOutage: (error: unknown) => boolean = isPreviewOutage,
): Promise<PromoCodeHandoff> {
  try {
    return { target: 'pro', quote: await preview.pro(code) }
  } catch (error) {
    if (isOutage(error)) return { target: 'unavailable', code }
  }
  try {
    return { target: 'studio', quote: await preview.studio(code) }
  } catch (error) {
    return isOutage(error) ? { target: 'unavailable', code } : { target: 'invalid', code }
  }
}
